// Provider requests for endpoints the webview cannot reach, sent from the app instead (desktop only).
//
// A hosted API that sends no CORS headers is blocked by the webview before the request leaves (api.ofox.ai
// answers the preflight with a bare 401), and nothing about the key or URL can fix that. Rust has no CORS:
// `provider_fetch_native` (src-tauri/src/native_fetch.rs) makes the same request and streams the reply back,
// and `nativeFetch` turns that stream into a real `Response`, so everything above the transport (SSE parsing,
// usage, errors, retries, the idle timeout) is unchanged.
//
// `providerFetch` decides which to use. Direct first; a webview network failure (`TypeError: Failed to
// fetch`) is retried ONCE natively, and if the server answers there at all, the endpoint's origin is
// learned so later requests go native from the start. A server that is actually down fails natively too,
// and the original error stands. The native attempt IS the reachability probe: a preflight-blocked request
// never left the webview, so the request is not sent twice. A connection cut also surfaces as that
// TypeError, after the request DID leave, so only a failure inside PREFLIGHT_FAIL_MS is retried.
//
// Same rule for local and public hosts: a local server without its CORS switch is learned too, which simply
// makes it work. The Test button's CORS advice only shows when even the native route fails.
//
// Rust refuses any URL that is not under a configured provider's base URL, from any of the three stores
// (LLM, image and voice providers), so this can only reach endpoints the user set up.
import type { HttpFetch } from "@aicc/inference";
import { httpGetBytes } from "./api";
import { base64ToBytes, bytesToBase64 } from "./base64";
import { endpointFixKey, endpointFixStore, type EndpointFix } from "./endpointFix";
import { Channel, invoke, isRemote } from "./rpc";

type NativeEvent =
  | { t: "head"; status: number; headers: Record<string, string> }
  | { t: "data"; b64: string }
  | { t: "end" }
  | { t: "error"; message: string };

/** Statuses a `Response` may not be given a body for. */
const NULL_BODY = new Set([101, 103, 204, 205, 304]);

const abortReason = (signal: AbortSignal): unknown =>
  signal.reason ?? new DOMException("The operation was aborted.", "AbortError");

/** The request body as Rust takes it: a string as text; anything else (a dictation upload's FormData) is
 *  serialized the way `fetch` would, multipart boundary header included, and sent as base64 bytes. */
async function wireBody(url: string, init: RequestInit, headers: Record<string, string>): Promise<{ body: string; body_b64?: string }> {
  if (init.body == null || typeof init.body === "string") return { body: init.body ?? "" };
  const r = new Request(url, { method: init.method ?? "POST", body: init.body });
  const ct = r.headers.get("content-type");
  if (ct && !Object.keys(headers).some((k) => k.toLowerCase() === "content-type")) headers["content-type"] = ct;
  return { body: "", body_b64: bytesToBase64(new Uint8Array(await r.arrayBuffer())) };
}

/** One request through `provider_fetch_native`, as a `Response` whose body streams. A network failure
 *  rejects with a TypeError, like `fetch`; an abort rejects with the signal's reason, like `fetch`. */
export const nativeFetch: HttpFetch = (url, init) =>
  new Promise<Response>((resolve, reject) => {
    const signal = init.signal ?? undefined;
    if (signal?.aborted) { reject(abortReason(signal)); return; }
    const id = crypto.randomUUID();
    let body: ReadableStreamDefaultController<Uint8Array> | null = null;
    let headed = false;
    let done = false;
    const stream = new ReadableStream<Uint8Array>({
      start: (c) => { body = c; },
      // The reader gave up early (a consumer broke out of the stream): stop the request in Rust too.
      cancel: () => { if (!done) { done = true; cleanup(); void invoke("provider_fetch_native_abort", { id }).catch(() => {}); } },
    });
    const cleanup = () => signal?.removeEventListener("abort", onAbort);
    const fail = (e: unknown) => {
      if (done) return;
      done = true;
      cleanup();
      if (headed) body?.error(e); else reject(e);
    };
    const onAbort = () => {
      void invoke("provider_fetch_native_abort", { id }).catch(() => {});
      fail(abortReason(signal!));
    };
    signal?.addEventListener("abort", onAbort, { once: true });

    const channel = new Channel<NativeEvent>();
    channel.onmessage = (m) => {
      if (done) return;
      if (m.t === "head") {
        headed = true;
        resolve(new Response(NULL_BODY.has(m.status) ? null : stream, { status: m.status, headers: m.headers }));
      } else if (m.t === "data") {
        body?.enqueue(base64ToBytes(m.b64));
      } else if (m.t === "end") {
        done = true;
        cleanup();
        body?.close();
      } else {
        fail(new TypeError(m.message));
      }
    };
    const headers = Object.fromEntries(new Headers(init.headers).entries());
    wireBody(url, init, headers)
      .then((b) => invoke("provider_fetch_native", { id, req: { url, method: init.method ?? "GET", headers, ...b }, onEvent: channel }))
      .catch((e) => fail(new TypeError(String(e))));
  });

// ---------------------------------------------------------------- which endpoints go native ---

const store = endpointFixStore(
  "native_fetch_fix",
  (_model, host) =>
    `${host} doesn't accept requests from inside an app, so Rin Chat now sends them from the app itself. Review it under Settings → Generation → Learned fixes.`,
);

/** The learned-fix key for a URL: its ORIGIN, since that is what CORS judges, so the model list and the
 *  chat calls of one endpoint share one entry. Null for a URL that does not parse. */
export function nativeFetchKey(url: string): string | null {
  try { return endpointFixKey(new URL(url).origin, ""); } catch { return null; }
}

export const loadNativeFetchFixes = (): Promise<void> => store.load();
export const allNativeFetchFixes = (): Array<{ key: string } & EndpointFix> => store.all();
export const clearNativeFetchFix = (key: string): void => store.clear(key);
/** Testing seam. */
export const resetNativeFetchFixes = (): void => store.reset();

// ---------------------------------------------------------------- what the Test button says ---

// Localhost AND private-range LAN IPs (10.x, 172.16-31.x, 192.168.x): a plain-HTTP LM Studio/Ollama on
// another machine is exactly what the presets recommend, and force-upgrading it to https made the URL
// impossible to save in working form. Public hosts still get the upgrade.
export const isLocalHost = (host: string) =>
  /^(localhost|127\.\d+\.\d+\.\d+|0\.0\.0\.0|\[::1\]|[^:/]+\.local|10\.\d+\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+|192\.168\.\d+\.\d+)(:|$)/i.test(host);

/** Was a webview "Failed to fetch" actually CORS? The webview hides the reason, but the Rust-side fetch
 *  has no CORS at all: if IT can reach the URL (any HTTP answer counts, even a 404), the server is up and
 *  the browser layer was blocked. Desktop only: a remote client's requests relay through the desktop and
 *  never hit CORS this way. */
async function corsProbe(url: string): Promise<boolean> {
  if (isRemote()) return false;
  try { await httpGetBytes(url); return true; } catch (e) { return /HTTP \d{3}/.test(String(e)); }
}

/** Which kind of webview "Failed to fetch" a Test hit, or null for any other error. Only reached when the
 *  native route failed as well, or on the phone remote. "local": the server is up and its CORS switch is
 *  off; "public": a hosted API that refuses in-app requests, which is the provider's side; "down". */
export async function fetchBlocked(e: unknown, base: string): Promise<"local" | "public" | "down" | null> {
  if (!/failed to fetch|networkerror|load failed/i.test(String(e))) return null;
  if (!(await corsProbe(`${base.replace(/\/+$/, "")}/models`))) return "down";
  let host = "";
  try { host = new URL(base).host; } catch { /* unparseable: public is the safer advice */ }
  return isLocalHost(host) ? "local" : "public";
}

/** ofox's OpenAI-compatible base URL. Its API sends no CORS headers on any route, so it is a preset. */
export const OFOX_BASE = "https://api.ofox.ai/v1";
/** Origins of presets known to refuse the webview, sent native from the first request instead of after
 *  a failed one. Not a learned fix, so it never shows under Learned fixes and cannot be cleared. */
const ALWAYS_NATIVE = new Set([new URL(OFOX_BASE).origin]);
/** A preflight refusal fails within a round trip or two. A TypeError later than this is a connection cut
 *  while the server may already be working on the request, and resending it would bill it twice.
 *  ponytail: a time gate, not a cause; a CORS failure behind a very slow DNS or TLS setup is not retried. */
const PREFLIGHT_FAIL_MS = 8000;

/** The desktop's provider transport: direct, or native for an endpoint that blocks the webview. */
export const providerFetch: HttpFetch = async (url, init) => {
  const key = nativeFetchKey(url);
  let origin = "";
  try { origin = new URL(url).origin; } catch { /* not a URL: the direct fetch reports it */ }
  if (!isRemote() && ALWAYS_NATIVE.has(origin)) return nativeFetch(url, init);
  if (key && store.has(key)) return nativeFetch(url, init);
  const started = Date.now();
  try {
    return await fetch(url, init);
  } catch (e) {
    // Only the webview's own network failure, and only a fast one. An abort is the caller's, and anything
    // else is not CORS.
    if (!(e instanceof TypeError) || init.signal?.aborted || !key || Date.now() - started > PREFLIGHT_FAIL_MS) throw e;
    let res: Response;
    try {
      res = await nativeFetch(url, init);
    } catch (native) {
      // Down for Rust too, or not a configured provider: the original failure is the honest one.
      throw init.signal?.aborted ? native : e;
    }
    store.note(key, "");
    return res;
  }
};
